import styled, { useTheme } from 'styled-components';

import type { ChartDataItem } from '../../../types/charts';
import { formatDate } from '../../../utils/dateUtils';
import type { PeriodBar } from '../../../utils/financesUtils';
import { FinancesPeriod } from '../../../utils/financesUtils';
import penceToPound from '../../../utils/penceToPound';
import ExportChart from '../Savings/ExportChart';
import ImportChart from '../Savings/ImportChart';

import DateNavigator from './DateNavigator';
import FinancesBarChart from './FinancesBarChart';

const SummaryLine = styled.p`
  font-size: 15px;
  margin-bottom: 0;
  font-weight: bold;
  text-align: center;
`;

interface Highlight {
  $color: string;
}

const Highlight = styled.span<Highlight>`
  color: ${(props) => props.$color};
`;

const Amount = styled.span`
  font-size: 18px;
  font-weight: bold;
`;

interface ChartCardProps {
  period: FinancesPeriod;
  anchorDate: Date;
  onNavigate: (date: Date) => void;
  dayChartData: ChartDataItem[] | null;
  periodBars: PeriodBar[] | null;
  exportTotal: number; // pence
  importTotal: number; // pence
  section: 'export' | 'import';
}

const ChartCard = ({
  period,
  anchorDate,
  onNavigate,
  dayChartData,
  periodBars,
  exportTotal,
  importTotal,
  section,
}: ChartCardProps) => {
  const theme = useTheme();
  const summaryPhrase = period === 'day' ? `on ${formatDate(anchorDate, 'dd/MM/yy')}` : `this ${period}`;
  const xAxisLabel = period === 'week' ? 'Day of the week' : period === 'year' ? 'Month' : 'Day of the month';

  const exportContent = (
    <>
      <DateNavigator anchorDate={anchorDate} period={period} onNavigate={onNavigate} />
      <SummaryLine>
        Total grid <Highlight $color={theme.colors.exported}>export</Highlight> earnings {summaryPhrase}:{' '}
        <Highlight $color={theme.colors.exported}>
          <Amount>£{penceToPound(exportTotal, false)}</Amount>
        </Highlight>
      </SummaryLine>
      <div className="mt-3">
        {period === 'day' && dayChartData ? (
          <ExportChart chartData={dayChartData} date={anchorDate.toISOString().slice(0, 10)} hideTitle compact />
        ) : periodBars ? (
          <FinancesBarChart
            bars={periodBars}
            dataKey="exportEarnings"
            barColor={theme.colors.exported}
            yAxisLabel="Export earnings"
            xAxisLabel={xAxisLabel}
          />
        ) : null}
      </div>
    </>
  );

  const importInner = (
    <>
      <DateNavigator anchorDate={anchorDate} period={period} onNavigate={onNavigate} />
      <SummaryLine>
        Total grid <Highlight $color={theme.colors.imported}>import</Highlight> costs {summaryPhrase}:{' '}
        <Highlight $color={theme.colors.imported}>
          <Amount>£{penceToPound(importTotal, false)}</Amount>
        </Highlight>
      </SummaryLine>
      <div className="mt-3">
        {period === 'day' && dayChartData ? (
          <ImportChart chartData={dayChartData} date={anchorDate.toISOString().slice(0, 10)} hideTitle compact />
        ) : periodBars ? (
          <FinancesBarChart
            bars={periodBars}
            dataKey="importCosts"
            barColor={theme.colors.imported}
            yAxisLabel="Import costs"
            xAxisLabel={xAxisLabel}
          />
        ) : null}
      </div>
    </>
  );

  if (section === 'export') return exportContent;
  return importInner;
};

export default ChartCard;
